import React from 'react'
import styled from 'styled-components'
import { useTranslation } from 'react-i18next'
import { Modal, theme } from '@lmc/ui-kit'
import { Divider } from '@lmc/ui-kit/src/layout'
import { Text } from '@lmc/ui-kit/src/general'
import { ImageSection } from 'components/generic/ListItem/components'
import { CarePlanAdlExtended } from 'components/generic/CarePlanSelector/CarePlanSelector'
import { NAMESPACE } from 'locale'
import { consentConstants } from 'locale/consent'

interface CarePlanLinkingConfirmationModalProps {
  isModalVisible: boolean
  setIsModalVisible: (visible: boolean) => void
  newlyLinkedCarePlans: CarePlanAdlExtended[]
  onConfirm: () => void
}

const CarePlanLinkingConfirmationModal: React.FC<
  CarePlanLinkingConfirmationModalProps
> = ({
  isModalVisible,
  setIsModalVisible,
  newlyLinkedCarePlans,
  onConfirm
}) => {
  const { t } = useTranslation(NAMESPACE.CONSENT)

  const handleConfirm = () => {
    onConfirm()
    setIsModalVisible(false)
  }

  return (
    <Modal
      isModalVisible={isModalVisible}
      setIsModalVisible={setIsModalVisible}
      onConfirm={handleConfirm}
      onCancel={handleConfirm}
      localisation={{
        title: t(consentConstants.CARE_PLAN_LINKING_MODAL_TITLE),
        confirmText: 'Confirm'
      }}
      testId="care-plan-linking-confirmation"
    >
      <ContentWrapper>
        <StyledDivider />
        <SuccessText level={2}>
          {t(consentConstants.CARE_PLAN_LINKING_MODAL_SUCCESS_MESSAGE)}
        </SuccessText>

        <NoReviewText level={2}>
          {t(consentConstants.CARE_PLAN_LINKING_MODAL_NO_REVIEW_TRIGGERED)}
        </NoReviewText>
        <CarePlansList>
          {newlyLinkedCarePlans.map((carePlan) => (
            <CarePlanItem key={carePlan.id}>
              <ImageSection
                popup
                iconColor={carePlan.color}
                icon={carePlan.icon}
              />
              <CarePlanName level={2}>{carePlan.name}</CarePlanName>
            </CarePlanItem>
          ))}
        </CarePlansList>
      </ContentWrapper>
    </Modal>
  )
}

const ContentWrapper = styled.div`
  display: flex;
  flex-direction: column;
`

const SuccessText = styled(Text)`
  font-size: 16px;
  margin-bottom: 24px;
`

const NoReviewText = styled(Text)`
  font-size: 16px;
  margin-bottom: 24px;
  font-weight: ${theme.font.weight.regular};
`

const StyledDivider = styled(Divider)`
  margin: 0 0 24px 0;
`

const CarePlansList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;
`

const CarePlanItem = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 4px 0;
`

const CarePlanName = styled(Text)`
  font-size: 16px;
  font-weight: 500;
  color: ${theme.color.neutral.color3};
`

export default CarePlanLinkingConfirmationModal
